import { useCallback, useDeferredValue, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { SplitSupplyContractListItem } from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import { useTranslation } from 'react-i18next'

import { MasterCrudTabShell } from '~/pages/AdminOcctoMaster/shell/MasterCrudTabShell'
import { useMasterCrudTab } from '~/pages/AdminOcctoMaster/shell/useMasterCrudTab'
import { CreateSplitSupplyContractModal } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/components/CreateSplitSupplyContractModal'
import { DeleteSplitSupplyContractDialog } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/components/DeleteSplitSupplyContractDialog'
import { EditSplitSupplyContractModal } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/components/EditSplitSupplyContractModal'
import { SplitSupplyContractTable } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/components/SplitSupplyContractTable'
import { useListSplitSupplyContracts } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/hooks/useListSplitSupplyContracts'
import { splitSupplyContractCsvColumns } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/csvColumns'
import type {
  SplitSupplyContractEditSnapshot,
  SplitSupplyContractRow,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/types'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'

function convertItem(
  item: SplitSupplyContractListItem,
): SplitSupplyContractRow {
  return {
    id: item.supplyPointSpecificNumber,
    supplyPointSpecificNumber: item.supplyPointSpecificNumber,
    areaCode: item.areaCode,
    customerName: item.customerName,
    supplyForm: item.supplyForm,
    ownSlotFrom: item.ownSlotFrom,
    ownSlotTo: item.ownSlotTo,
    loadFollowing: {
      code: item.loadFollowing?.businessCode ?? '',
      name: item.loadFollowing?.businessName ?? '',
    },
    base: {
      code: item.base?.businessCode ?? '',
      name: item.base?.businessName ?? '',
    },
    selfRole: item.selfRole,
    updatedBy: item.updatedBy,
    updatedAt: item.updatedAt,
  }
}

/** エリア + 検索 (供給地点特定番号・需要家・両側の事業者のコードと名称) で絞る。 */
function filterRows(
  rows: SplitSupplyContractRow[],
  areaCode: AreaCode,
  searchText: string,
): SplitSupplyContractRow[] {
  const trimmed = searchText.trim().toLowerCase()
  return rows.filter((row) => {
    if (areaCode !== AreaCode.UNSPECIFIED && row.areaCode !== areaCode) {
      return false
    }
    if (trimmed === '') return true
    return [
      row.supplyPointSpecificNumber,
      row.customerName ?? '',
      row.loadFollowing.code,
      row.loadFollowing.name,
      row.base.code,
      row.base.name,
    ].some((text) => text.toLowerCase().includes(trimmed))
  })
}

/**
 * 分割供給契約タブ。 一覧 (供給地点特定番号の昇順) + 登録・編集・削除。
 * 1 つの供給地点を 2 者の小売で分担する取り決めと、 通告値の初期値となる基本パターンを管理する。
 */
export const SplitSupplyContractTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListSplitSupplyContracts()

  const [selectedAreaCode, setSelectedAreaCode] = useState<AreaCode>(
    AreaCode.UNSPECIFIED,
  )
  const [searchText, setSearchText] = useState('')
  const [editSnapshot, setEditSnapshot] =
    useState<SplitSupplyContractEditSnapshot>()
  const deferredSearchText = useDeferredValue(searchText)

  const rows = useMemo(
    () => (data !== undefined ? data.items.map(convertItem) : []),
    [data],
  )
  const filteredRows = useMemo(
    () => filterRows(rows, selectedAreaCode, deferredSearchText),
    [rows, selectedAreaCode, deferredSearchText],
  )

  // 有効期間を持たないマスタなので失効フィルタは無い (expiry オプション未指定)。
  const tab = useMasterCrudTab<SplitSupplyContractRow>({
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix: 'page:adminOcctoMaster.splitSupplyContract.serverError',
    filteredRows,
    csv: {
      columns: splitSupplyContractCsvColumns(),
      fileBase: 'split-supply-contract',
    },
  })

  const deleteSnapshot =
    tab.modalState.kind === 'delete' ? editSnapshot : undefined
  const openDeleteFromEdit = useCallback(
    (snapshot: SplitSupplyContractEditSnapshot) => {
      setEditSnapshot(snapshot)
      tab.openDelete(snapshot.row)
    },
    [tab],
  )

  return (
    <MasterCrudTabShell
      createModal={
        // 条件 mount (閉じたら form ごと破棄): 次に開いた時に前回の入力が残らない。
        tab.modalState.kind === 'create' ? (
          <CreateSplitSupplyContractModal isOpen onClose={tab.closeModal} />
        ) : null
      }
      csvExportDisabledReason={tab.csvExportDisabledReason}
      deleteDialog={
        deleteSnapshot !== undefined ? (
          <DeleteSplitSupplyContractDialog
            errorMessage={tab.deleteError?.message}
            isOpen
            onClose={tab.closeModal}
            onError={(message) => {
              tab.setDeleteError({ message, retriable: true })
            }}
            snapshot={deleteSnapshot}
          />
        ) : null
      }
      editModal={
        tab.modalState.kind === 'edit' ? (
          <EditSplitSupplyContractModal
            row={tab.modalState.row}
            isOpen
            onClose={tab.closeModal}
            onDelete={(snapshot) => {
              openDeleteFromEdit(snapshot)
            }}
          />
        ) : null
      }
      filterToolbar={
        <MasterFilterToolbar
          onSearchTextChange={setSearchText}
          onSelectArea={setSelectedAreaCode}
          searchPlaceholder={t(
            'page:adminOcctoMaster.splitSupplyContract.toolbar.searchPlaceholder',
          )}
          searchText={searchText}
          selectedAreaCode={selectedAreaCode}
        />
      }
      onAddIndividual={tab.openCreate}
      onCsvExport={tab.csvExportEnabled ? tab.handleCsvExport : undefined}
      showInitialLoading={tab.showInitialLoading}
      table={
        <SplitSupplyContractTable
          errorLabel={tab.errorLabel}
          hasContracts={rows.length > 0}
          isLoading={isLoading}
          onEdit={tab.openEdit}
          rows={tab.visibleRows}
        />
      }
    />
  )
}
